import React, { useRef } from 'react';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { MoveInSlotInfo } from '../../services/tenantApi';
import {
  CALENDAR_HOURS,
  formatDayHeader,
  formatHourLabel,
  getSlotStyle,
  HOUR_HEIGHT,
  TOTAL_GRID_HEIGHT,
} from './moveInAppointmentFormatters';

export function MoveInWeekCalendar({
  dates,
  slotsByDate,
  loading,
  todayET,
  booking,
  onBook,
  onPrevWeek,
  onNextWeek,
  canGoBack,
  canGoForward = true,
  scheduledDateStart,
  actualAppointmentStart,
  blockedDates,
}: {
  dates: string[];
  slotsByDate: Record<string, MoveInSlotInfo[]>;
  loading: boolean;
  todayET: string;
  booking: boolean;
  onBook: (slot: MoveInSlotInfo) => void;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  canGoBack: boolean;
  canGoForward?: boolean;
  scheduledDateStart?: string | null;
  actualAppointmentStart?: string | null;
  blockedDates?: Set<string>;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const weekRangeLabel = dates.length > 0
    ? (() => {
        const first = formatDayHeader(dates[0]);
        const last = formatDayHeader(dates[dates.length - 1]);
        return `${first.dateLabel} – ${last.dateLabel}`;
      })()
    : '';

  const toDateHour = (iso: string | null | undefined) => {
    if (!iso) return { date: null as string | null, hour: null as number | null };
    const d = new Date(iso);
    return {
      date: d.toLocaleDateString('en-CA', { timeZone: 'America/New_York' }),
      hour: parseInt(d.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', hour12: false }), 10),
    };
  };

  const sched = toDateHour(scheduledDateStart);
  const actual = toDateHour(actualAppointmentStart);

  const isBeforeFloor = (dateStr: string, hour: number): boolean => {
    if (!sched.date || sched.hour === null) return false;
    if (dateStr < sched.date) return true;
    if (dateStr === sched.date && hour < sched.hour) return true;
    return false;
  };

  const isScheduledSlot = (dateStr: string, hour: number): boolean => {
    return dateStr === sched.date && hour === sched.hour;
  };

  const isCurrentApptSlot = (dateStr: string, hour: number): boolean => {
    return dateStr === actual.date && hour === actual.hour;
  };

  const scheduledAndCurrentSame =
    sched.date === actual.date && sched.hour === actual.hour;

  const navBar = (
    <div className="flex items-center justify-between mb-3">
      <button
        onClick={onPrevWeek}
        disabled={!canGoBack}
        className={`flex items-center gap-1 text-sm font-medium px-2 py-1 rounded-md transition-colors ${
          canGoBack ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100' : 'text-gray-300 cursor-not-allowed'
        }`}
      >
        <ChevronLeft size={16} />
        <span>Earlier</span>
      </button>
      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{weekRangeLabel}</span>
      <button
        onClick={onNextWeek}
        disabled={!canGoForward}
        className={`flex items-center gap-1 text-sm font-medium px-2 py-1 rounded-md transition-colors ${
          canGoForward ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100' : 'text-gray-300 cursor-not-allowed'
        }`}
      >
        <span>Later</span>
        <ChevronRight size={16} />
      </button>
    </div>
  );

  if (loading) {
    return (
      <div>
        {navBar}
        <div className="flex items-center justify-center py-16">
          <Loader2 size={24} className="animate-spin text-red-700 dark:text-red-400" />
          <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Loading availability...</span>
        </div>
      </div>
    );
  }

  const findSlotForHour = (dateStr: string, hour: number): MoveInSlotInfo | undefined => {
    const daySlots = slotsByDate[dateStr] ?? [];
    return daySlots.find((s) => {
      const d = new Date(s.startTime);
      const etHour = parseInt(
        d.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', hour12: false }),
        10
      );
      return etHour === hour;
    });
  };

  return (
    <div>
      {navBar}

      <div className="border border-gray-200 dark:border-white/10 rounded-lg overflow-hidden bg-white dark:bg-gray-800 relative">
        {booking && (
          <div className="absolute inset-0 z-20 bg-white/70 flex flex-col items-center justify-center gap-2">
            <Loader2 size={28} className="animate-spin text-red-700 dark:text-red-400" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Booking your appointment...</span>
          </div>
        )}
        <div
          className="grid bg-gray-50 dark:bg-white/5 border-b border-gray-200 dark:border-white/10 sticky top-0 z-10"
          style={{ gridTemplateColumns: `48px repeat(${dates.length}, 1fr)` }}
        >
          <div className="h-14 border-r border-gray-200 dark:border-white/10" />
          {dates.map((d) => {
            const { dayOfWeek, dateLabel } = formatDayHeader(d);
            const isPast = d < todayET;
            const isToday = d === todayET;
            return (
              <div
                key={d}
                className={`flex flex-col items-center justify-center h-14 text-xs border-r border-gray-200 last:border-r-0 ${
                  isPast ? 'text-gray-300' : isToday ? 'text-red-700 font-bold' : 'text-gray-700'
                }`}
              >
                <span className="font-semibold">{dayOfWeek}</span>
                <span className={isToday ? 'bg-red-700 text-white rounded-full px-1.5 text-[10px] leading-4' : ''}>{dateLabel}</span>
              </div>
            );
          })}
        </div>

        <div ref={scrollRef} className="overflow-y-auto" style={{ maxHeight: `${HOUR_HEIGHT * 10}px` }}>
          <div className="relative" style={{ height: `${TOTAL_GRID_HEIGHT}px` }}>
            {CALENDAR_HOURS.map((hour, i) => (
              <div
                key={hour}
                className="absolute left-0 right-0 border-b border-gray-100 dark:border-white/5"
                style={{ top: `${i * HOUR_HEIGHT}px`, height: `${HOUR_HEIGHT}px` }}
              >
                <div className="absolute left-0 w-12 h-full flex items-center justify-end pr-1.5 border-r border-gray-200 dark:border-white/10">
                  <span className="text-[10px] text-gray-400 dark:text-gray-500 leading-none">{formatHourLabel(hour)}</span>
                </div>
              </div>
            ))}

            <div
              className="absolute top-0 bottom-0 right-0"
              style={{ left: '48px', display: 'grid', gridTemplateColumns: `repeat(${dates.length}, 1fr)` }}
            >
              {dates.map((dateStr) => {
                const isPast = dateStr < todayET;
                return (
                  <div key={dateStr} className="relative border-r border-gray-100 dark:border-white/5 last:border-r-0">
                    {CALENDAR_HOURS.map((hour, i) => {
                      const slot = findSlotForHour(dateStr, hour);
                      const full = slot ? slot.available === 0 : true;
                      const noSlot = !slot;
                      const beforeFloor = isBeforeFloor(dateStr, hour);
                      const isCurrent = isCurrentApptSlot(dateStr, hour);
                      const isScheduled = isScheduledSlot(dateStr, hour);
                      const isSpecial = isCurrent || isScheduled;
                      const isBlocked = blockedDates?.has(dateStr) && !isCurrent && !isScheduled;
                      const unavailable = isPast || noSlot || (full && !isSpecial) || beforeFloor || isBlocked;

                      const avail = slot?.available ?? 0;
                      const { bg, hoverBg, text: textColor, label: slotLabel } = getSlotStyle(avail, slot?.capacity ?? 5);

                      let cellClass: string;
                      if (isPast || beforeFloor || isBlocked) {
                        cellClass = 'bg-gray-50/60 cursor-not-allowed';
                      } else if (isCurrent && slot) {
                        cellClass = 'bg-blue-50 ring-2 ring-inset ring-blue-400 hover:bg-blue-100 cursor-pointer';
                      } else if (isScheduled && !scheduledAndCurrentSame && slot) {
                        cellClass = 'bg-purple-50 ring-2 ring-inset ring-purple-400 hover:bg-purple-100 cursor-pointer';
                      } else if (noSlot) {
                        cellClass = 'bg-gray-50/40 cursor-not-allowed';
                      } else {
                        cellClass = `${bg} ${unavailable ? 'cursor-not-allowed' : `${hoverBg} cursor-pointer`}`;
                      }

                      let slotTitle: string;
                      if (isBlocked) {
                        slotTitle = 'Not available for booking on this day';
                      } else if (beforeFloor) {
                        slotTitle = 'Before your first available time slot';
                      } else if (isCurrent && slot) {
                        slotTitle = `Current move-in appointment · ${formatHourLabel(hour)} – ${formatHourLabel(hour + 1)} ET`;
                      } else if (isScheduled && !scheduledAndCurrentSame && slot) {
                        slotTitle = `First available time slot · ${formatHourLabel(hour)} – ${formatHourLabel(hour + 1)} ET`;
                      } else if (slot && !isPast) {
                        slotTitle = `${formatHourLabel(hour)} – ${formatHourLabel(hour + 1)} ET · ${slot.available} of ${slot.capacity} available`;
                      } else if (isPast) {
                        slotTitle = 'Past date';
                      } else {
                        slotTitle = '';
                      }

                      let cellLabel: React.ReactNode = null;
                      if ((beforeFloor || isBlocked) && !isPast) {
                        cellLabel = <span className="text-[10px] font-medium text-gray-300">—</span>;
                      } else if (!isPast && isCurrent && slot) {
                        cellLabel = (
                          <span className="text-[10px] font-semibold text-blue-700 leading-tight text-center px-0.5">
                            Current Appt.
                          </span>
                        );
                      } else if (!isPast && isScheduled && !scheduledAndCurrentSame && slot) {
                        cellLabel = (
                          <span className="text-[10px] font-semibold text-purple-700 leading-tight text-center px-0.5">
                            First Available
                          </span>
                        );
                      } else if (!isPast && slot) {
                        cellLabel = (
                          <span className={`text-[10px] font-medium leading-tight text-center px-0.5 ${textColor}`}>
                            {slotLabel}
                          </span>
                        );
                      }

                      return (
                        <button
                          key={hour}
                          disabled={(unavailable && !isSpecial) || booking}
                          onClick={() => slot && onBook(slot)}
                          className={`absolute left-0 right-0 border-b border-gray-100 flex items-center justify-center transition-colors ${cellClass}`}
                          style={{ top: `${i * HOUR_HEIGHT}px`, height: `${HOUR_HEIGHT}px` }}
                          title={slotTitle}
                        >
                          {cellLabel}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center flex-wrap gap-x-4 gap-y-1 py-2 border-t border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-[10px] text-gray-500 dark:text-gray-400">
          {actualAppointmentStart && (
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-50 border-2 border-blue-400" /> Current Appt.</span>
          )}
          {scheduledDateStart && !scheduledAndCurrentSame && (
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-purple-50 border-2 border-purple-400" /> First Available</span>
          )}
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900/40" /> Open</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-yellow-50 border border-yellow-200" /> Limited</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-orange-100 border border-orange-200" /> Almost full</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-100 dark:bg-red-900/40 border border-red-200 dark:border-red-900/40" /> Unavailable</span>
          <span>All times Eastern</span>
        </div>
      </div>
    </div>
  );
}
